import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useSubscribe } from '../utils/subscribeProvider';
import UserAvatar from './UserAvatar';
import VerificationBadge from '../components/VerificationBadge';
import IDVerificationBadge from '../components/IdVerificationBadge';

const BG = '#08090B';
const CARD = '#23262C';
const ORANGE = '#FF8C08';

function formatCount(count) {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return String(count);
}

export default function UserItem({ user }) {
  const navigate = useNavigate();
  const subscribe = useSubscribe();
  const [isSeller, setIsSeller] = useState(false);

  const uid = user.id?.toString() || '';
  const displayName = user.displayName?.toString() || user.name?.toString() || 'Unknown';

  const rawUsername = user.username?.toString() || '';
  const username = rawUsername ? (rawUsername.startsWith('@') ? rawUsername : `@${rawUsername}`) : '';

  const avatarUrl = user.profileImage?.toString() || user.photoURL?.toString() || '';

  // Firestore stores this field as `isVerified`, not `verified`.
  // Falling back to `verified` too, in case some older docs use that key.
  const verified = user.isVerified === true || user.verified === true;

  const isVerifiedSeller = user.isVerifiedSeller === true || user.isVerifiedSeller === 'true';

  // Reads the seller category type (Personal vs Business&Organizations) from
  // the nested `verification` map on the user document, matching how
  // ProfileScreen resolves it. Used only to color the ID Verified badge —
  // falls back to a flat `categoryType` field if present.
  const verification = user.verification;
  const nestedType = verification && typeof verification === 'object' ? verification.categoryType : null;
  const categoryType = nestedType ?? user.categoryType;
  const isBusinessCategory = categoryType === 'Business&Organizations';

  const subscriberRaw = user.subscribersCount ?? user.subscribers;
  const subscriberCount = Array.isArray(subscriberRaw)
    ? subscriberRaw.length
    : typeof subscriberRaw === 'number'
    ? Math.trunc(subscriberRaw)
    : 0;
  // subscriberCount / formatCount kept available for callers that want to
  // render it elsewhere; the original widget computes but doesn't display it.
  void formatCount(subscriberCount);

  const currentUid = auth.currentUser?.uid || '';
  const isSelf = currentUid === uid;

  useEffect(() => {
    let cancelled = false;

    async function checkIsSeller() {
      if (!uid) return;
      try {
        const q = query(collection(db, 'listings'), where('uid', '==', uid), limit(1));
        const snap = await getDocs(q);
        if (!cancelled) setIsSeller(!snap.empty);
      } catch (e) {
        if (!cancelled) setIsSeller(false);
      }
    }

    checkIsSeller();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const openProfile = () => {
    if (uid) navigate(`/profile/${uid}`);
  };

  const handleSubscribeToggle = async (e) => {
    e.stopPropagation();
    if (!auth.currentUser) return;
    await subscribe.toggleSubscribe(uid);
  };

  const showIdBadge = isVerifiedSeller;
  const showSellerBadge = isSeller;

  const isSubscribed = subscribe?.isSubscribedTo?.(uid);
  const shouldSubBack = subscribe?.shouldSubBack?.(uid);

  let buttonText;
  if (isSubscribed) buttonText = 'Subscribed';
  else if (shouldSubBack) buttonText = 'Sub Back';
  else buttonText = 'Subscribe';

  return (
    <div
      onClick={openProfile}
      style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: BG,
        padding: '12px 16px',
        cursor: 'pointer',
      }}
    >
      {/* Avatar */}
      <UserAvatar size={52} imageUrl={avatarUrl || undefined} name={displayName} />
      <div style={{ width: 12, flexShrink: 0 }} />

      {/* Name + username + badges */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Name row with verification badge */}
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <span
            style={{
              color: '#fff',
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </span>
          {verified && (
            <>
              <div style={{ width: 4, flexShrink: 0 }} />
              <VerificationBadge size={16} />
            </>
          )}
        </div>

        {/* Username */}
        {username && (
          <div
            style={{
              marginTop: 2,
              color: 'rgba(255,255,255,0.45)',
              fontSize: 13,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {username}
          </div>
        )}

        {/* Badges row (ID Verified + Listed property) */}
        {(showIdBadge || showSellerBadge) && (
          <div
            style={{
              marginTop: 6,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
            }}
          >
            {showIdBadge && <IDVerificationBadge fontSize={9} isBusiness={isBusinessCategory} />}
            {showSellerBadge && (
              <span
                style={{
                  backgroundColor: '#2ECC71',
                  borderRadius: 12,
                  padding: '3px 8px',
                  color: '#fff',
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                Listed property
              </span>
            )}
          </div>
        )}
      </div>

      <div style={{ width: 10, flexShrink: 0 }} />

      {/* Subscribe button (hidden for own profile) */}
      {!isSelf && uid && (
        <button
          onClick={handleSubscribeToggle}
          style={{
            padding: '7px 16px',
            borderRadius: 8,
            border: isSubscribed ? '1px solid rgba(255,255,255,0.15)' : 'none',
            backgroundColor: isSubscribed ? CARD : ORANGE,
            cursor: 'pointer',
            transition: 'background-color 200ms ease-in-out',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              color: isSubscribed ? 'rgba(255,255,255,0.70)' : '#000',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {buttonText}
          </span>
        </button>
      )}
    </div>
  );
}

export { UserItem };